/**
 * Il vocabolario dei soldi, in un posto solo: come si chiama uno stato del
 * contratto, di che colore è la sua pillola, come si fanno i conti del piano e
 * come si scrive il promemoria di pagamento. Contratti e Fatture-incassi leggono
 * da qui.
 *
 * I conti si fanno in CENTESIMI INTERI. L'API manda gli importi come stringhe
 * esatte («15790.92»); sommati come numeri in virgola mobile, 0,1 + 0,2 fa
 * 0,30000000000000004 e un piano da 15.790,92 € «non torna» di un millesimo.
 */

/* Gli stati del contratto (0025, patto col cruscotto). Il colore segue il
   significato che ha in tutta l'app: attenzione = serve un gesto, info = in
   corso, ok = chiuso bene. */
import { messaggioErrore as messaggioErroreComune } from "./preventivi";

export const STATI_CONTRATTO = {
  bozza: { label: "Bozza", tab: "Bozze", pill: "neutro" },
  da_firmare: { label: "Da firmare", tab: "Da firmare", pill: "attenzione" },
  firmato: { label: "Firmato", tab: "Firmati, in attesa d'acconto", pill: "info" },
  in_produzione: { label: "In produzione", tab: "In produzione", pill: "info" },
  consegnato: { label: "Consegnato", tab: "Consegnati", pill: "info" },
  concluso: { label: "Concluso", tab: "Conclusi", pill: "ok" },
  annullato: { label: "Annullato", tab: "Annullati", pill: "neutro" },
};
export const ORDINE_STATI = ["bozza", "da_firmare", "firmato", "in_produzione", "consegnato", "concluso", "annullato"];
export const statoContratto = (s) => STATI_CONTRATTO[s === "aperto" ? "bozza" : s] || STATI_CONTRATTO.bozza;
/** Firmato = uno di questi stati, mai «c'è una data di firma» (0025, 0030). */
export const FIRMATI = new Set(["firmato", "in_produzione", "consegnato", "concluso"]);

/* Il bottone che porta a quello stato: una frase da showroom, non il nome tecnico. */
export const AZIONE_VERSO = {
  da_firmare: "Pronto da firmare",
  firmato: "Segna come firmato",
  in_produzione: "Segna in produzione",
  consegnato: "Segna consegnato",
  concluso: "Segna concluso",
  bozza: "Torna in bozza",
  annullato: "Annulla il contratto",
};

export const RATE = {
  primo_acconto: "Primo acconto",
  secondo_acconto: "Secondo acconto",
  terzo_acconto: "Terzo acconto",
  saldo: "Saldo",
};

export const FORMULE_SALDO = {
  prima_consegna: { label: "Saldo prima della consegna", causale: "Saldo prima della consegna" },
  dopo_montaggio: { label: "Saldo dopo il montaggio", causale: "Saldo dopo il montaggio" },
};

/* Come IdA incassa da adesso (call del 28-10, DECISIONI D11): un acconto alla firma, il 30%
   dopo il rilievo, il saldo prima della consegna; per qualcuno il 10% finale. Il 30%
   comprende l'acconto della firma: se Aleandro dice di no, si cambia `comprendeAcconto`
   e basta, il modello non cambia. Sono proposte: ogni importo si riscrive. */
export const PROPOSTA = { dopoRilievo: 30, comprendeAcconto: true, finale: 10 };

/** In centesimi: la rata dopo il rilievo e il 10% finale proposti per quel totale e quell'acconto. */
export function propostaPiano(totaleCent, accontoCent) {
  if (totaleCent === null || totaleCent === undefined) return { dopoRilievo: null, finale: null };
  const trenta = Math.round((totaleCent * PROPOSTA.dopoRilievo) / 100);
  const dopoRilievo = trenta - (PROPOSTA.comprendeAcconto ? accontoCent || 0 : 0);
  return { dopoRilievo: dopoRilievo > 0 ? dopoRilievo : null, finale: Math.round((totaleCent * PROPOSTA.finale) / 100) };
}

const NOMI_MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto",
  "settembre", "ottobre", "novembre", "dicembre"];
/** «2027-01-01» → «gennaio 2027»: della consegna voluta conta il mese (0046). */
export const meseConsegna = (iso) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(iso || ""));
  return m ? `${NOMI_MESI[Number(m[2]) - 1]} ${m[1]}` : null;
};

export const STATI_RATA = {
  in_ritardo: { pill: "errore" },
  in_scadenza: { pill: "attenzione" },
  aperta: { pill: "neutro" },
  da_fissare: { pill: "neutro" },
  pagata: { pill: "ok" },
};

/** Il nome dello stato della pillola → il tono dell'etichetta del sistema nuovo (StatusBadge). */
export const TONO = { ok: "success", attenzione: "warning", errore: "danger", info: "brand", neutro: "neutral" };

export const STATI_FATTURA = {
  da_emettere: { label: "Da emettere", pill: "attenzione" },
  in_invio: { label: "In invio", pill: "info" },
  emessa: { label: "Emessa", pill: "info" },
  pagata: { label: "Pagata", pill: "ok" },
  annullata: { label: "Annullata", pill: "neutro" },
};

export const METODI_INCASSO = [
  { codice: "bonifico", label: "Bonifico" },
  { codice: "contanti", label: "Contanti" },
  { codice: "assegno", label: "Assegno" },
  { codice: "carta", label: "Carta" },
  { codice: "finanziamento", label: "Finanziamento" },
  { codice: "altro", label: "Altro" },
];

/* ── Centesimi ───────────────────────────────────────────────────────────── */

/** «15790.92» (dall'API) → 1579092. `null` se non è un importo. */
export function centesimiDaApi(valore) {
  if (valore === null || valore === undefined || valore === "") return null;
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(String(valore).trim());
  if (!m) return null;
  const cent = Number(m[2]) * 100 + Number((m[3] || "").padEnd(2, "0"));
  return m[1] ? -cent : cent;
}

/**
 * Un importo scritto da una persona: «3.000», «3000,50», «3 000 €», «7790.92».
 * Il punto con tre cifre dopo è il separatore delle migliaia; la virgola è il
 * decimale. Più di due decimali → `null`: un centesimo non si arrotonda in silenzio.
 */
export function centesimiDaTesto(testo) {
  let s = String(testo ?? "").replace(/[€\s ]/g, "");
  if (!s) return null;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if ((s.match(/\./g) || []).length > 1 || /^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return centesimiDaApi(s);
}

/** 1579092 → «15790.92», per mandare all'API. */
export function centesimiInApi(cent) {
  if (cent === null || cent === undefined) return null;
  const segno = cent < 0 ? "-" : "";
  const a = Math.abs(cent);
  return `${segno}${Math.floor(a / 100)}.${String(a % 100).padStart(2, "0")}`;
}

/** 1579092 → «15790,92», da rimettere in un campo di testo. */
export function centesimiInCampo(cent) {
  const s = centesimiInApi(cent);
  return s === null ? "" : s.replace(".", ",");
}

/* ── Date ───────────────────────────────────────────────────────────────── */

const fmtChiave = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "Europe/Rome" });
export const oggiRoma = () => fmtChiave.format(new Date());

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
/** «2026-10-01» → «1 ottobre 2026» (senza passare da `new Date`, niente fusi). */
export function dataLunga(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return null;
  return `${Number(m[3])} ${MESI[Number(m[2]) - 1]} ${m[1]}`;
}

/** Quello che si legge accanto a una rata aperta. */
export function quandoRata(r) {
  if (r.stato === "in_ritardo") return r.giorni_ritardo === 1 ? "in ritardo da 1 giorno" : `in ritardo da ${r.giorni_ritardo} giorni`;
  if (r.stato === "da_fissare") return "data da fissare";
  return `scade il ${dataLunga(r.scadenza)}`;
}

/* ── Il promemoria su WhatsApp ───────────────────────────────────────────── */

/** Il numero come lo vuole wa.me: solo cifre, col prefisso del paese. */
export function numeroWhatsApp(telefono) {
  let s = String(telefono || "").replace(/[^\d+]/g, "");
  if (!s) return null;
  if (s.startsWith("+")) s = s.slice(1);
  else if (s.startsWith("00")) s = s.slice(2);
  else if (/^3\d{8,9}$/.test(s)) s = `39${s}`; // cellulare italiano scritto senza prefisso
  else if (/^0\d{5,10}$/.test(s)) s = `39${s}`; // fisso italiano
  return /^\d{8,15}$/.test(s) ? s : null;
}

/**
 * Il link che apre WhatsApp col promemoria già scritto. Lo manda una persona:
 * il sistema non spedisce niente. Il testo è modificabile prima di inviare.
 */
export function linkPromemoria(r, { eur }) {
  const numero = numeroWhatsApp(r.telefono);
  if (!numero) return null;
  const nome = (r.nome_proprio || "").trim();
  const quando = r.scadenza ? `, prevista per il ${dataLunga(r.scadenza)},` : "";
  const testo =
    `Buongiorno${nome ? ` ${nome}` : ""}, qui Interni D'Autore. ` +
    `Un promemoria gentile: la rata «${r.causale}» di ${eur(r.residuo)}${quando} risulta ancora da saldare. ` +
    "Se l'avete già pagata, non tenete conto di questo messaggio. " +
    "Per qualsiasi cosa siamo allo 0872 983283. Grazie!";
  return `https://wa.me/${numero}?text=${encodeURIComponent(testo)}`;
}

/* ── Gli errori del backend ──────────────────────────────────────────────── */

/**
 * La frase da mostrare. Un 422 di validazione di FastAPI ha `detail` = ELENCO di
 * oggetti: passato così a un toast fa cadere la pagina (in produzione: bianca).
 * Il 409 dei contratti ha `detail` = { messaggio, contratto_id }.
 */
// All'unione dei rami del 24-09 ne è rimasta una: quella di lib/preventivi.js (dice il campo in
// italiano invece del messaggio inglese di pydantic). Il nome resta per chi lo usa già.
export function messaggioErrore(e) {
  return messaggioErroreComune(e, "Qualcosa non ha funzionato");
}
